import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { Button } from '../../components/UI';
import { ArtistPricingWidget, PricingPath } from '../../components/pricing_widget/ArtistPricingWidget.component';
import { useEmbed } from '../book/useEmbed';

const logo = (mode: 'light' | 'dark'): string =>
    `https://${process.env.REACT_APP_AWS_BUCKET_IMAGES || ''}.s3.us-west-1.amazonaws.com/dashboard-images/Wallmates-Logo-${mode}-mode.png`;

const COPY: Record<PricingPath, { title: string; body: string; button: string }> = {
    brand: {
        title: 'Ready to sell on your own store?',
        body: 'Create your account, add your designs and connect Shopify or our API to switch on Store Owner pricing.',
        button: 'Start as a Brand for $0'
    },
    artist: {
        title: 'Ready to license your designs?',
        body: 'Create your account, upload a design, set its licensing price and connect Stripe to get paid.',
        button: 'Get started as an Artist'
    }
};

// #region PublicPricing
/**
 * Public, no-login pricing. On its own it is a full page; with ?embed=1 inside an
 * iframe (see public/embed/pricing.js) it is just the widget, and the host page
 * decides what "Get started" opens. ?path=artist opens on the Artist side.
 */
const PublicPricing: React.FunctionComponent = (): React.ReactElement => {
    const [params] = useSearchParams();
    const embedded = useEmbed(params);
    const navigate = useNavigate();
    const initialPath: PricingPath = params.get('path') === 'artist' ? 'artist' : 'brand';

    React.useEffect(() => { document.title = 'Pricing · Wallmates'; }, []);

    const getStarted = (path: PricingPath) => {
        if (embedded) {
            window.parent.postMessage({ type: 'wallmates:pricing_cta', path }, '*');
            return;
        }
        navigate(`/get-started?src=${encodeURIComponent(params.get('src') || `pricing_page_${path}`)}`);
    };

    const widget = (
        <ArtistPricingWidget
            variant="public"
            initialPath={initialPath}
            footer={(path) => <GetStartedStrip path={path} onClick={() => getStarted(path)} />}
        />
    );

    if (embedded) return <div data-embed-root className="p-px">{widget}</div>;

    return (
        <div className="min-h-screen bg-surface-sunken px-4 py-6 sm:py-10">
            <div className="mx-auto w-full max-w-6xl">
                <a href="https://wallmates.com" className="mb-6 inline-block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600">
                    <img className="block h-[29px] w-[129px] dark:hidden" src={logo('light')} alt="Wallmates" />
                    <img className="hidden h-[29px] w-[129px] dark:block" src={logo('dark')} alt="Wallmates" />
                </a>
                {widget}
            </div>
        </div>
    );
};

const GetStartedStrip: React.FunctionComponent<{ path: PricingPath; onClick: () => void }> = ({ path, onClick }): React.ReactElement => {
    const copy = COPY[path];
    return (
        <div className="flex flex-col gap-5 rounded-2xl bg-blue-600 p-6 text-white sm:flex-row sm:items-center sm:justify-between sm:p-8">
            <div>
                <p className="font-display text-2xl font-semibold leading-tight tracking-tight sm:text-3xl">{copy.title}</p>
                <p className="mt-2 max-w-xl text-sm text-white/90">{copy.body}</p>
            </div>
            {/* The portal's "white" button follows the dark theme; on the azure card it must stay white. */}
            <Button color="white" onClick={onClick} className="shrink-0 dark:[--btn-bg:theme(colors.white)]">
                {copy.button}
                <ArrowRightIcon data-slot="icon" />
            </Button>
        </div>
    );
};

export default PublicPricing;
